import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import "./ProfileSettings.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const ProfileSettings = ({ user, updateUser, setMessage }) => {
  // Profile settings state
  const [profileData, setProfileData] = useState({
    username: user?.username || "",
    gender: user?.gender || "",
  });

  // Profile image state
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const profileImageRef = useRef(null);

  // Hero image state
  const [heroImage, setHeroImage] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const heroImageRef = useRef(null);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Loading state
  const [saving, setSaving] = useState(false);

  // Update profile data based on user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        username: user.username || "",
        gender: user.gender || "",
      });
    }
  }, [user]);

  // Profile picture functions
  const handleProfileImageClick = () => {
    profileImageRef.current.click();
  };

  const handleProfileImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Hero image functions
  const handleHeroImageClick = () => {
    heroImageRef.current.click();
  };

  const handleHeroImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Show original file size
      console.log(
        `Original file: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(
          2
        )}MB`
      );

      // Set a temporary preview immediately for better UX
      const tempReader = new FileReader();
      tempReader.onloadend = () => {
        setHeroPreview(tempReader.result);
        setMessage({ type: "info", text: "Optimizing image..." });
      };
      tempReader.readAsDataURL(file);

      try {
        // Only compress if file is larger than 1MB
        if (file.size > 1 * 1024 * 1024) {
          const options = {
            maxSizeMB: 0.8, // Target size in MB
            maxWidthOrHeight: 1920, // Max width/height
            useWebWorker: true,
            onProgress: (progress) => {
              setCompressionProgress(progress);
            },
          };

          const compressedFile = await imageCompression(file, options);
          console.log(
            `Compressed file size: ${(
              compressedFile.size /
              1024 /
              1024
            ).toFixed(2)}MB`
          );

          // Generate a new file name to avoid caching issues
          const fileExtension = file.name.split(".").pop();
          const newFileName = `hero-image-${Date.now()}.${fileExtension}`;

          // Create a new file object with the compressed data
          const optimizedFile = new File([compressedFile], newFileName, {
            type: compressedFile.type,
          });

          setHeroImage(optimizedFile);

          // Update preview with compressed image
          const reader = new FileReader();
          reader.onloadend = () => {
            setHeroPreview(reader.result);
            setMessage({
              type: "success",
              text: `Image optimized! Original: ${(
                file.size /
                1024 /
                1024
              ).toFixed(2)}MB, Compressed: ${(
                optimizedFile.size /
                1024 /
                1024
              ).toFixed(2)}MB`,
            });

            // Auto-clear message after 5 seconds
            setTimeout(() => {
              setMessage({ type: "", text: "" });
            }, 5000);
          };
          reader.readAsDataURL(optimizedFile);
        } else {
          // File is already small enough, no compression needed
          setHeroImage(file);
          setMessage({
            type: "info",
            text: "Image already optimized (under 1MB)",
          });

          // Auto-clear message after 3 seconds
          setTimeout(() => {
            setMessage({ type: "", text: "" });
          }, 3000);
        }
      } catch (error) {
        console.error("Error compressing image:", error);
        // Fallback to original file
        setHeroImage(file);
        setMessage({
          type: "warning",
          text: "Could not optimize image. Using original file.",
        });
      }
    }
  };

  // Form change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    setUploadProgress(0);

    console.log("Form submission started");

    try {
      const formData = new FormData();

      // Add text fields
      formData.append("username", profileData.username);
      formData.append("gender", profileData.gender);

      // Add images if selected
      if (profileImage) {
        console.log("Adding profile image to form data:", profileImage.name);
        formData.append("profilePicture", profileImage);
      }

      if (heroImage) {
        console.log(
          "Adding hero image to form data:",
          heroImage.name,
          `(${(heroImage.size / 1024 / 1024).toFixed(2)}MB)`
        );
        formData.append("heroImage", heroImage);
      }

      console.log("Sending request to:", `${API_BASE_URL}/api/users/profile`);

      const response = await axios.patch(
        `${API_BASE_URL}/api/users/profile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          timeout: 60000, // Increased timeout to 60 seconds
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
            console.log(`Upload progress: ${percentCompleted}%`);
          },
        }
      );

      console.log("Response received:", response.data);

      if (response.data.success) {
        // Update user in context
        updateUser(response.data.data);

        // Reset image states
        setProfileImage(null);
        setProfilePreview(null);
        setHeroImage(null);
        setHeroPreview(null);

        // Show success message
        setMessage({
          type: "success",
          text: "Profile updated successfully!",
        });
      }
    } catch (error) {
      console.error("Failed to update settings:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);

      let errorMessage = "Failed to update settings";

      if (error.code === "ECONNABORTED") {
        errorMessage =
          "Request timed out. The image may be too large or the connection too slow.";
      } else if (error.response) {
        errorMessage = error.response.data?.error || errorMessage;
      }

      setMessage({
        type: "error",
        text: errorMessage,
      });
    } finally {
      console.log("Form submission complete");
      setSaving(false);
      setUploadProgress(0);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      <div className="settings-section">
        <h3>Profile Pictures</h3>
        <div className="profile-images">
          <div className="profile-image-section">
            <h4>Profile Picture</h4>
            <div
              className="profile-image-container"
              onClick={handleProfileImageClick}
            >
              <img
                src={profilePreview || user?.profilePicture}
                alt="Profile"
                className="profile-image"
              />
              <div className="image-overlay">
                <span>Change</span>
              </div>
            </div>
            <input
              type="file"
              ref={profileImageRef}
              onChange={handleProfileImageChange}
              accept="image/*"
              style={{ display: "none" }}
            />
            {profileImage && (
              <div className="selected-file">
                <span>{profileImage.name}</span>
              </div>
            )}
          </div>

          <div className="profile-image-section">
            <h4>Banner Image</h4>
            <div
              className="hero-image-container"
              onClick={handleHeroImageClick}
            >
              <img
                src={heroPreview || user?.heroImage}
                alt="Banner"
                className="hero-image"
              />
              <div className="image-overlay">
                <span>Change</span>
              </div>
            </div>
            <input
              type="file"
              ref={heroImageRef}
              onChange={handleHeroImageChange}
              accept="image/*"
              style={{ display: "none" }}
            />

            {heroImage && (
              <div className="selected-file">
                <span>
                  {heroImage.name} (
                  {(heroImage.size / 1024 / 1024).toFixed(2)}MB)
                </span>

                {/* Compression progress bar */}
                {compressionProgress > 0 && compressionProgress < 100 && (
                  <>
                    <div className="progress-container">
                      <div
                        className="progress-bar"
                        style={{ width: `${compressionProgress}%` }}
                      ></div>
                    </div>
                    <div className="progress-info">
                      <span>Optimizing image...</span>
                      <span>{compressionProgress}%</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Personal Information</h3>
        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            type="text"
            id="username"
            name="username"
            value={profileData.username}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="gender">Gender</label>
          <select
            id="gender"
            name="gender"
            value={profileData.gender}
            onChange={handleInputChange}
          >
            <option value="">Prefer not to say</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {saving && uploadProgress > 0 && (
        <div className="settings-section">
          <h3>Uploading...</h3>
          <div className="progress-container">
            <div
              className="progress-bar"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div className="progress-info">
            <span>Please wait while we update your profile</span>
            <span>{uploadProgress}%</span>
          </div>
        </div>
      )}

      <div className="form-actions">
        <button
          type="submit"
          className="save-settings-btn"
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

export default ProfileSettings;